import { Eye, EyeOff, ListTree, RotateCcw } from "lucide-react";
import { tocEntries } from "../../lib/content";

/**
 * Réglage du sommaire (éditeur) : activé ou non, avec ou sans sous-titres (H3),
 * libellés modifiables et titres masquables. Le texte des titres dans le cours ne change pas.
 * toc = { enabled, includeH3, items: { [idDuTitre]: { label?, hidden? } } }
 */
export default function TocPanel({ html, toc, onChange }) {
  const value = { enabled: false, includeH3: false, items: {}, ...(toc || {}) };
  const entries = tocEntries(html, value);
  const setItem = (id, patch) => {
    const items = { ...value.items, [id]: { ...(value.items[id] || {}), ...patch } };
    if (!items[id].label && !items[id].hidden) delete items[id];
    onChange({ ...value, items });
  };
  return (
    <div className="space-y-3" data-testid="toc-panel">
      <label className="flex items-center justify-between gap-3 cursor-pointer">
        <span className="text-sm text-zinc-200 inline-flex items-center gap-2"><ListTree className="w-4 h-4 text-zinc-400" /> Afficher un sommaire</span>
        <button
          type="button"
          role="switch"
          aria-checked={value.enabled}
          onClick={() => onChange({ ...value, enabled: !value.enabled })}
          className={`relative w-10 h-6 shrink-0 rounded-full transition-colors ${value.enabled ? "bg-emerald-500" : "bg-white/15"}`}
          data-testid="toc-switch"
        >
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${value.enabled ? "left-[18px]" : "left-0.5"}`} />
        </button>
      </label>
      {!value.enabled ? (
        <p className="text-xs text-zinc-500 leading-relaxed">Utile pour les textes longs : la liste des intertitres s'affiche en haut, et le lecteur saute directement à une partie.</p>
      ) : (
        <>
          <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={value.includeH3} onChange={(e) => onChange({ ...value, includeH3: e.target.checked })} className="accent-emerald-500" />
            Inclure les sous-titres (H3)
          </label>
          {entries.length === 0 ? (
            <p className="text-xs text-amber-300/90">Ajoutez des intertitres (H2) dans le texte : ils formeront le sommaire.</p>
          ) : (
            <div className="space-y-1.5">
              {entries.map((e) => (
                <div key={e.id} className={`flex items-center gap-1.5 ${e.level === 3 ? "pl-4" : ""}`}>
                  <input
                    value={value.items[e.id]?.label || ""}
                    onChange={(ev) => setItem(e.id, { label: ev.target.value })}
                    placeholder={e.text}
                    title="Libellé dans le sommaire (laisser vide pour reprendre le titre)"
                    className={`flex-1 min-w-0 h-8 rounded-md bg-white/[0.03] border border-white/10 px-2 text-sm placeholder:text-zinc-400 focus:outline-none focus:border-emerald-500/50 ${e.hidden ? "opacity-40 line-through" : ""}`}
                  />
                  {e.custom && (
                    <button type="button" onClick={() => setItem(e.id, { label: "" })} title="Reprendre le titre du texte" className="p-1 text-zinc-500 hover:text-zinc-200"><RotateCcw className="w-3.5 h-3.5" /></button>
                  )}
                  <button type="button" onClick={() => setItem(e.id, { hidden: !e.hidden })} title={e.hidden ? "Afficher dans le sommaire" : "Masquer du sommaire"} className="p-1 text-zinc-500 hover:text-zinc-200">
                    {e.hidden ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                </div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-zinc-500 leading-relaxed">Le sommaire suit l'ordre des intertitres du texte. Changer un libellé ici ne modifie pas le titre dans le texte.</p>
        </>
      )}
    </div>
  );
}
